import {
  PageBlueprint,
  createFrontendPlugin,
} from '@backstage/frontend-plugin-api';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
// IMPORTANT: `TechDocsAddonLocations` (the runtime VALUE) lives in the
// package's main entry, NOT in `/alpha`. The `/alpha` build only
// re-exports it as a TypeScript type (see
// `node_modules/@backstage/plugin-techdocs-react/dist/alpha.esm.js`,
// which exports just `AddonBlueprint` and
// `attachTechDocsAddonComponentData`). Importing
// `TechDocsAddonLocations` from `/alpha` compiles fine but evaluates
// to `undefined` at runtime, which crashes here with
// `TypeError: Cannot read properties of undefined (reading 'Content')`.
// Two imports from the same package is the supported pattern.
import { AddonBlueprint } from '@backstage/plugin-techdocs-react/alpha';
import { TechDocsAddonLocations } from '@backstage/plugin-techdocs-react';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import { MkDocsLayoutFix } from '../../components/techdocs/MkDocsLayoutFix';

/**
 * Custom replacement for the stock `page:techdocs` extension shipped by
 * `@backstage/plugin-techdocs/alpha`. We override it because the stock
 * loader (`TechDocsIndexPageContent`) wraps everything in `<Container>`
 * from `@backstage/ui` (the new BUI design system), which does TWO
 * things that break for us:
 *
 *   1) `max-width: 120rem` (= 1920px) + `margin-inline: auto`. On any
 *      viewport between the sidebar width and 1920px the content gets
 *      centred with mismatched gutters because the BUI breakpoint
 *      logic doesn't see Backstage's sidebar — the result is a giant
 *      empty stripe to the left of the filters with the table squashed
 *      into the middle of the screen.
 *   2) `padding-inline` from CSS variables that are defined by `bui-*`
 *      design tokens but NOT inherited inside Material-UI's themed
 *      tree, so the table either touches the page edges or sits on a
 *      column that isn't aligned with /catalog and /apis.
 *
 * Wrapping the same `EntityListProvider` + `CatalogFilterLayout`
 * components in `<Content>` from `@backstage/core-components` (the
 * exact same chrome `/catalog`'s `BaseCatalogPage` uses) is enough to
 * reuse the spacing and breakpoints the rest of the app is designed
 * around.
 *
 * Both the original page AND the original nav item are disabled in
 * `app-config.yaml`:
 *
 *     extensions:
 *       - page:techdocs: false
 *       - nav-item:techdocs: false
 *
 * Without `page:techdocs: false` both pages would attach to `/docs`
 * and react-router would render whichever was mounted first. Without
 * `nav-item:techdocs: false` we'd see a duplicate "Docs" entry in
 * the sidebar — the upstream nav item points at the disabled page's
 * `rootRouteRef`, so it would either be a dead link or just sit there
 * next to the working one.
 *
 * This module re-uses the upstream `techdocsPlugin.routes.root`
 * (see ⚠️ comment further down) and attaches `title: 'Docs'` +
 * `icon: <LibraryBooksIcon />` to the `PageBlueprint`. The framework
 * auto-derives a sidebar nav entry from those — same mechanism
 * `kindsModule` and `permissionsModule` rely on.
 *
 * Note we deliberately do NOT pass `noHeader: true`. We want our
 * `BoschPageLayout` banner ("Documentation" + subtitle from the
 * `PAGE_OVERRIDES` map in `pageLayout.tsx`) to render. The loader
 * below intentionally does NOT call `<Header>` itself — it starts
 * straight at `<Content>`.
 *
 * ⚠️ ROUTE REF — REUSE THE UPSTREAM ONE.
 *
 * We pass `techdocsPlugin.routes.root` here instead of a fresh
 * `createRouteRef()`. Same reason as `catalogModule`: when the user
 * opens any TechDocs page (an entity's "Docs" tab, or the global
 * /docs index), the renderer calls `useRouteRef(techdocs.root)` to
 * generate the link back to the docs index. Without re-using the
 * upstream routeRef, that resolver throws `No path for
 * routeRef{id=techdocs.root}` at runtime. See the matching block in
 * `packages/app/src/modules/catalog/index.tsx`.
 */
const docsPage = PageBlueprint.make({
  params: {
    path: '/docs',
    title: 'Docs',
    icon: <LibraryBooksIcon />,
    routeRef: techdocsPlugin.routes.root,
    loader: () =>
      import('../../components/techdocs/TechDocsHomePage').then(m => (
        <m.TechDocsHomePage />
      )),
  },
});

/**
 * TechDocs Addon that injects a small CSS override INSIDE the docs
 * reader's Shadow DOM, pushing the MkDocs left-nav and right-TOC
 * sidebars below the entity-card row that `BoschPageLayout` renders
 * ABOVE the docs body. See
 * `packages/app/src/components/techdocs/MkDocsLayoutFix.tsx` for the
 * full geometric reasoning.
 *
 * The blueprint is wired via `AddonBlueprint` from
 * `@backstage/plugin-techdocs-react/alpha`. Internally it attaches to
 * `api:techdocs/addons` (the same input the upstream techdocs plugin
 * exposes), so the fix runs whenever the reader page renders — there's
 * no opt-in step required by the user. Location `Content` is the
 * "virtual" location that allows mutating arbitrary DOM inside the
 * shadow root (vs. `Header` / `Subheader` / `PrimarySidebar` etc.,
 * which would render an actual UI slot we don't need).
 */
const mkdocsLayoutFixAddon = AddonBlueprint.make({
  name: 'mkdocs-layout-fix',
  params: {
    name: 'mkdocs-layout-fix',
    location: TechDocsAddonLocations.Content,
    component: MkDocsLayoutFix,
  },
});

/**
 * Registered as a frontend PLUGIN (not a module) because nav-item
 * auto-discovery from `PageBlueprint` only fires reliably when the
 * page lives inside a `createFrontendPlugin`. Same reason `kindsModule`
 * and `permissionsModule` are plugins. `pluginId: 'techdocs-override'`
 * avoids colliding with the upstream `techdocs` pluginId.
 *
 * No `routes` field — we don't expose any new route refs, we just
 * re-bind the upstream `techdocsPlugin.routes.root` via the
 * PageBlueprint above.
 *
 * The `mkdocsLayoutFixAddon` ships in the same plugin even though it
 * has nothing to do with the `/docs` index page — keeping all our
 * techdocs customisations in one place makes it obvious where to look
 * when the reader layout misbehaves again. The addon's `attachTo`
 * targets the upstream techdocs plugin's `api:techdocs/addons`
 * extension, so co-locating it here is purely an organisational
 * choice, not a structural requirement.
 */
export const techdocsModule = createFrontendPlugin({
  pluginId: 'techdocs-override',
  extensions: [docsPage, mkdocsLayoutFixAddon],
});
